import { useCallback } from "react";
import useSWR, { useSWRConfig } from "swr";
import { useApiClient } from "@/hooks/useApiClient";
import {
  type MapBounds,
  type SaleDocumentDetail,
  type SaleDocumentListResponse,
  type SaleDownloadResponse,
  type SalePinListResponse,
  type SalePurchaseListResponse,
  type SalePurchaseResponse,
} from "../types";
import { isPinSearchableBounds, toPinQuery } from "../utils/bounds";

/** 販売中の物件の一覧・検索のAPI（地図の住居表示検索からも使う）。 */
export const SALE_DOCUMENTS_URL = "real-estate-document-fetch/sale/documents/";
const BASE_URL = SALE_DOCUMENTS_URL;
const PURCHASES_URL = "real-estate-document-fetch/sale/purchases/";

/** 一覧の1ページの件数。 */
export const SALE_PAGE_SIZE = 20;

interface SwrResult<T> {
  data: T | undefined;
  isLoading: boolean;
  error: unknown;
  mutate: () => Promise<unknown>;
}

/**
 * 販売中のリユース資料（黒塗り）を、マンション名・住所・地番・家屋番号の部分一致で検索する。
 * @param keyword - 検索語（空白区切りは AND）
 * @param offset - 取得開始位置
 * @returns 一覧（件数つき）
 */
export const useSaleDocuments = (
  keyword: string,
  offset: number
): SwrResult<SaleDocumentListResponse> => {
  const { apiClient } = useApiClient();
  const { data, isLoading, error, mutate } = useSWR<
    SaleDocumentListResponse,
    unknown
  >(
    [BASE_URL, keyword, offset],
    async () =>
      (
        await apiClient.get<SaleDocumentListResponse>(BASE_URL, {
          params: { q: keyword, offset, limit: SALE_PAGE_SIZE },
        })
      ).data,
    // 検索語・ページが変わったら前の結果を残さない（条件と違う物件を選べてしまうため）
    { revalidateOnFocus: false }
  );
  return { data, isLoading, error, mutate };
};

/**
 * 地図の表示範囲にあるピンを取得する。範囲が広すぎる場合は問い合わせない。
 * @param bounds - 表示範囲。未確定の間は null
 * @returns ピン。広すぎる範囲・未確定の間は data が undefined
 */
export const useSalePins = (
  bounds: MapBounds | null
): SwrResult<SalePinListResponse> => {
  const { apiClient } = useApiClient();
  const searchable = bounds !== null && isPinSearchableBounds(bounds);
  const { data, isLoading, error, mutate } = useSWR<
    SalePinListResponse,
    unknown
  >(
    searchable ? [BASE_URL, "pins", toPinQuery(bounds)] : null,
    async () =>
      (
        await apiClient.get<SalePinListResponse>(`${BASE_URL}pins/`, {
          params: toPinQuery(bounds as MapBounds),
        })
      ).data,
    // 前の範囲のピンを残さない（別の範囲や、広すぎて取得しない範囲で、範囲外のピンを選べてしまうため）
    { revalidateOnFocus: false }
  );
  return { data, isLoading, error, mutate };
};

/**
 * 購入前の物件詳細（購入できるPDFの一覧つき）を取得する。
 * @param documentId - 物件ID。未指定の場合は取得しない
 * @returns 詳細
 */
export const useSaleDocumentDetail = (
  documentId: number | null
): SwrResult<SaleDocumentDetail> => {
  const { apiClient } = useApiClient();
  const { data, isLoading, error, mutate } = useSWR<
    SaleDocumentDetail,
    unknown
  >(
    documentId !== null ? [BASE_URL, "detail", documentId] : null,
    async () =>
      (
        await apiClient.get<SaleDocumentDetail>(
          `${BASE_URL}${String(documentId)}/`
        )
      ).data,
    { revalidateOnFocus: false }
  );
  return { data, isLoading, error, mutate };
};

/** 購入履歴の1ページの件数。 */
export const PURCHASE_PAGE_SIZE = 10;

/**
 * 購入したリユース資料（黒塗り）の履歴（本人と、同じ会社で共有が許可された人の購入）を、購入日の新しい順に取得する。
 * @param offset - 取得開始位置
 * @returns 履歴（件数つき）
 */
export const useSalePurchases = (
  offset: number
): SwrResult<SalePurchaseListResponse> => {
  const { apiClient } = useApiClient();
  // キーの先頭を BASE_URL にそろえ、購入成功時の一覧・ピンの再検証（購入済みの表示）と一緒に取り直す
  const { data, isLoading, error, mutate } = useSWR<
    SalePurchaseListResponse,
    unknown
  >(
    [BASE_URL, "purchases", offset],
    async () =>
      (
        await apiClient.get<SalePurchaseListResponse>(PURCHASES_URL, {
          params: { offset, limit: PURCHASE_PAGE_SIZE },
        })
      ).data,
    { revalidateOnFocus: false }
  );
  return { data, isLoading, error, mutate };
};

interface SaleActions {
  /** 1PDFを購入する。購入済みなら課金されない（charged=false）。 */
  purchase: (
    documentId: number,
    fileId: number
  ) => Promise<SalePurchaseResponse>;
  /** 購入済みのPDFの、期限つきダウンロードURLを取得する。 */
  fetchDownload: (
    documentId: number,
    fileId: number
  ) => Promise<SaleDownloadResponse>;
}

/**
 * 購入とダウンロードURLの取得。
 * @returns purchase / fetchDownload
 */
export const useSaleActions = (): SaleActions => {
  const { apiClient } = useApiClient();
  const { mutate } = useSWRConfig();
  const purchase = useCallback<SaleActions["purchase"]>(
    async (documentId, fileId) => {
      const result = (
        await apiClient.post<SalePurchaseResponse>(
          `${BASE_URL}${documentId}/purchase/`,
          { fileId }
        )
      ).data;
      // 購入済みの表示（一覧・地図のピン）を最新にする。詳細は呼び出し側が再取得するので対象にしない
      mutate(
        (key) =>
          Array.isArray(key) && key[0] === BASE_URL && key[1] !== "detail"
      ).catch(() => undefined);
      return result;
    },
    [apiClient, mutate]
  );
  const fetchDownload = useCallback<SaleActions["fetchDownload"]>(
    async (documentId, fileId) =>
      (
        await apiClient.get<SaleDownloadResponse>(
          `${BASE_URL}${documentId}/download/`,
          { params: { fileId } }
        )
      ).data,
    [apiClient]
  );
  return { purchase, fetchDownload };
};
